'use client';

import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import { useTheme } from 'next-themes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSignOut } from '@/features/authentication/hooks/useSignOut';
import { DishUi } from '@/features/dish-management/types';
import { EmployeeUi } from '@/features/user-management/types';
import { getPrimaryRole } from '@/lib/auth/getPrimaryRole';
import { useAppUser } from '@/lib/context/AppUserContext';
import { UserRole } from '@/lib/enums';
import { getDishesQueryConfig, getQueryConfig } from '@/lib/tanstackQuery/queryRegistry';
import { trackingManager } from '@/lib/tracking/tracking-manager-instance';

import { useCommandPalette } from '../components/command-provider';
import {
  ActionType,
  NavigateParams,
  SearchResult,
  TrackActionParams,
  TrackClosedParams,
} from '../types';

export function useCommandPaletteManager(userRoles: UserRole[] = []) {
  // State
  const [searchValue, setSearchValue] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const actionExecutedRef = useRef(false);
  const openedAtRef = useRef<number | null>(null);

  // Context & external hooks
  const { isOpen, close } = useCommandPalette();
  const router = useRouter();
  const { user } = useAppUser();
  const { setTheme } = useTheme();
  const { signOut } = useSignOut();
  const locale = useLocale();

  // Role checks
  const isAdmin = userRoles.includes(UserRole.admin);
  const canAccessDishes =
    userRoles.includes(UserRole.admin) || userRoles.includes(UserRole.kitchen);

  // Debounce effect (from use-command-search)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchValue.toLowerCase().trim());
    }, 200);
    return () => clearTimeout(timer);
  }, [searchValue]);

  // Queries (from use-command-search)
  // Fetch employees - only when command palette is open (lazy loading)
  const { queryKey: employeeQueryKey, queryFn: employeeQueryFn } = getQueryConfig('employees');
  const { data: employeesRaw, isLoading: isLoadingEmployees } = useQuery({
    queryKey: employeeQueryKey,
    queryFn: employeeQueryFn,
    staleTime: 5 * 60 * 1000,
    enabled: isAdmin && isOpen,
  });

  // Fetch dishes - only when command palette is open (lazy loading)
  const { queryKey: dishQueryKey, queryFn: dishQueryFn } = getDishesQueryConfig(locale);
  const { data: dishesRaw, isLoading: isLoadingDishes } = useQuery({
    queryKey: dishQueryKey,
    queryFn: dishQueryFn,
    staleTime: 5 * 60 * 1000,
    enabled: canAccessDishes && isOpen,
  });

  // Filter employees based on search term - only for admins
  const employeeResults = useMemo((): SearchResult[] => {
    if (!isAdmin || !debouncedTerm || debouncedTerm.length < 2) return [];

    const employees = Array.isArray(employeesRaw) ? (employeesRaw as EmployeeUi[]) : [];

    return employees
      .filter((emp) => {
        const searchFields = [emp.name, emp.email, emp.internal_number, emp.company, emp.canteen]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchFields.includes(debouncedTerm);
      })
      .slice(0, 5)
      .map((emp) => ({
        id: emp.id,
        type: 'employee' as const,
        label: emp.name,
        description: [emp.email, emp.company].filter(Boolean).join(' \u2022 '),
        href: `/app/employee-management?search=${encodeURIComponent(emp.name)}`,
      }));
  }, [employeesRaw, debouncedTerm, isAdmin]);

  // Filter dishes based on search term - for admin and kitchen
  const dishResults = useMemo((): SearchResult[] => {
    if (!canAccessDishes || !debouncedTerm || debouncedTerm.length < 2) return [];

    const dishes = Array.isArray(dishesRaw) ? (dishesRaw as DishUi[]) : [];

    return dishes
      .filter((dish) => {
        const searchFields = [dish.name, dish.description, dish.original_name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchFields.includes(debouncedTerm);
      })
      .slice(0, 5)
      .map((dish) => ({
        id: dish.id ?? '',
        type: 'dish' as const,
        label: dish.name,
        description: dish.description,
        href: `/app/menu-management/dishes?search=${encodeURIComponent(dish.name)}`,
      }));
  }, [dishesRaw, debouncedTerm, canAccessDishes]);

  // Tracking helpers (from use-command-palette-tracking)
  const trackOpened = useCallback(
    (trigger: 'keyboard' | 'button') => {
      openedAtRef.current = Date.now();
      trackingManager.trackEvent('COMMAND_PALETTE_OPENED', {
        trigger,
        role: getPrimaryRole(user?.role),
      });
    },
    [user?.role],
  );

  const trackClosed = useCallback(({ method, searchTerm, hadResults }: TrackClosedParams) => {
    const durationMs = openedAtRef.current ? Date.now() - openedAtRef.current : 0;
    openedAtRef.current = null;

    trackingManager.trackEvent('COMMAND_PALETTE_CLOSED', {
      method,
      searchTerm: searchTerm || undefined,
      hadResults,
      durationMs,
    });
  }, []);

  const trackAction = useCallback(
    ({ actionType, actionId, searchTerm, resultPosition, totalResults }: TrackActionParams) => {
      trackingManager.trackEvent('COMMAND_PALETTE_ACTION', {
        actionType,
        actionId,
        searchTerm: searchTerm || undefined,
        resultPosition: resultPosition ?? undefined,
        totalResults: totalResults ?? undefined,
      });

      // Reset timer after action (will be closed via navigation)
      openedAtRef.current = null;
    },
    [],
  );

  // Reset state helper
  const resetState = useCallback(() => {
    close();
    setSearchValue('');
  }, [close]);

  // Handlers (from use-command-palette-actions)
  const handleNavigate = useCallback(
    ({ href, actionType, actionId }: NavigateParams, navigationCommandsCount: number = 0) => {
      actionExecutedRef.current = true;
      trackAction({
        actionType,
        actionId,
        searchTerm: searchValue || undefined,
        totalResults: employeeResults.length + dishResults.length + navigationCommandsCount,
      });
      resetState();
      router.push(href);
    },
    [resetState, router, trackAction, searchValue, employeeResults.length, dishResults.length],
  );

  const handleLanguageChange = useCallback(
    async (newLocale: string) => {
      actionExecutedRef.current = true;
      trackAction({
        actionType: 'language',
        actionId: `language-${newLocale}`,
        searchTerm: searchValue || undefined,
      });
      resetState();
      try {
        if (user) {
          const response = await fetch('/api/user/language', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ language: newLocale }),
          });
          if (!response.ok) throw new Error('Failed to update language');
        } else {
          document.cookie = `bite-club-locale=${newLocale}; path=/; max-age=${60 * 60 * 24 * 365}`;
        }
        router.refresh();
      } catch (error) {
        console.error('Error updating language:', error);
      }
    },
    [resetState, router, user, trackAction, searchValue],
  );

  const handleSettingsAction = useCallback(
    async (action: string, commandId: string) => {
      actionExecutedRef.current = true;

      const actionType: ActionType = action === 'logout' ? 'logout' : 'theme';
      trackAction({
        actionType,
        actionId: commandId,
        searchTerm: searchValue || undefined,
      });

      resetState();

      switch (action) {
        case 'theme-light':
          setTheme('light');
          break;
        case 'theme-dark':
          setTheme('dark');
          break;
        case 'theme-system':
          setTheme('system');
          break;
        case 'language': {
          const newLocale = commandId.replace('language-', '');
          await handleLanguageChange(newLocale);
          break;
        }
        case 'logout':
          await signOut();
          break;
      }
    },
    [resetState, setTheme, handleLanguageChange, signOut, trackAction, searchValue],
  );

  const handleOpenChange = useCallback(
    (open: boolean, navigationCommandsCount: number = 0) => {
      if (!open) {
        const hasResults = employeeResults.length > 0 || dishResults.length > 0;
        if (!actionExecutedRef.current) {
          trackClosed({
            method: 'escape',
            searchTerm: searchValue || undefined,
            hadResults: hasResults || navigationCommandsCount > 0,
          });
        }
        actionExecutedRef.current = false;
        resetState();
      }
    },
    [resetState, trackClosed, searchValue, employeeResults.length, dishResults.length],
  );

  const isLoading = isLoadingEmployees || isLoadingDishes;
  const hasEmployeeResults = employeeResults.length > 0;
  const hasDishResults = dishResults.length > 0;
  const hasResults = hasEmployeeResults || hasDishResults;

  return {
    // State
    searchValue,
    setSearchValue,
    isOpen,
    debouncedTerm,

    // Search results
    employeeResults,
    dishResults,
    hasEmployeeResults,
    hasDishResults,
    hasResults,
    isLoading,

    // Role checks
    isAdmin,
    canAccessDishes,

    // Handlers
    handleNavigate,
    handleLanguageChange,
    handleSettingsAction,
    handleOpenChange,

    // Tracking (exposed for external use if needed)
    trackOpened,
    trackAction,
    trackClosed,
  };
}
